import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { successToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { useAbortEffect } from '@shared/hooks/useAbortEffect';
import { useIsOpenWithData } from '@shared/hooks/useIsOpenWithData';
import { reportAppError } from '@shared/reportAppError';
import { CENTS_IN_DOLLAR } from '@utils/currency';
import { OPERATIONS_SHIFTS_PATH } from 'restaurantAdmin/paths';
import { useRestaurant } from '../../../context/useRestaurant';
import {
  editListing,
  type EditListingPayload,
  getListing,
} from '../apiHelpers';
import { useListingsContext } from '../ListingsContext';
import type { ListingFormData } from '../types';
import { getDefaultFormValues } from '../utils/formHelpers';
import { getListingPagePath } from '../utils/listingUtils';

export const useEditListing = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { listingId } = useParams();
  const { id: restaurantId } = useRestaurant();
  const {
    floorPlans,
    refreshFloorPlans,
    refreshListings,
    setSelectedFloorPlanId,
    setSelectedListingId,
  } = useListingsContext();

  const { data: existingListing, isPending } = useAbortEffect(
    (signal) => getListing(listingId!, restaurantId, signal),
    [restaurantId, listingId],
  );

  const { control, handleSubmit, setValue, resetField } =
    useForm<ListingFormData>({
      values: getDefaultFormValues({
        floorPlan: floorPlans.find(
          (fp) => existingListing?.floorPlanId === fp.id,
        )!,
        isListingLoading: isPending,
        listing: existingListing || null,
      }),
    });

  const {
    isOpen: isErrorModalOpen,
    data: errorModalData,
    open: openErrorModal,
    close: closeErrorModal,
  } = useIsOpenWithData<{ message: string }>();
  const {
    isOpen: isWarningModalOpen,
    data: warningModalData,
    open: openWarningModal,
    close: closeWarningModal,
  } = useIsOpenWithData<{ warnings: string[] }>();

  const buildPayload = ({
    data,
    ignoreWarnings,
  }: {
    data: ListingFormData;
    ignoreWarnings: boolean;
  }): EditListingPayload => ({
    floorPlanId: data.floorPlanId,
    highlightedFloorPlanTableIds: data.highlightedTables.map(
      (table) => table.id,
    ),
    iconName: data.iconName,
    maximumGuests: Number(data.maximumGuests),
    minimumGuests: Number(data.minimumGuests),
    name: data.name,
    price: Number(data.price) * CENTS_IN_DOLLAR,
    publicName: data.publicName,
    turnTime: data.turnTime,
    shiftIds: data.shiftIds,
    ignoreWarnings,
  });

  const handleOnClickSave = handleSubmit(async (data: ListingFormData) => {
    const isDraft = existingListing?.status === 'draft';

    try {
      const response = await editListing(
        restaurantId,
        listingId!,
        buildPayload({ data, ignoreWarnings: false }),
      );

      if (response.warnings.length) {
        openWarningModal({ warnings: response.warnings });
      } else {
        const userFacingEntityName = hideListingsTerminology
          ? 'Table'
          : 'Listing';
        successToast({
          message: `${userFacingEntityName} successfully updated`,
        });
        refreshListings();
        refreshFloorPlans();
        setSelectedFloorPlanId(data.floorPlanId);
        setSelectedListingId(listingId!);
        const destinationPath = hideListingsTerminology
          ? OPERATIONS_SHIFTS_PATH
          : getListingPagePath(isDraft ? 'draft' : 'published');
        navigate(destinationPath);
      }
    } catch (e) {
      if (e instanceof ApiError) {
        openErrorModal({ message: e.message });
      }
      reportAppError(e);
    }
  });

  const handleOnClickConfirm = handleSubmit(async (data: ListingFormData) => {
    const isDraft = existingListing?.status === 'draft';

    try {
      await editListing(
        restaurantId,
        listingId!,
        buildPayload({ data, ignoreWarnings: true }),
      );

      successToast({
        message: 'Listing successfully updated',
      });
      refreshListings();
      refreshFloorPlans();
      setSelectedFloorPlanId(data.floorPlanId);
      setSelectedListingId(listingId!);
      navigate(getListingPagePath(isDraft ? 'draft' : 'published'));
    } catch (e) {
      if (e instanceof ApiError) {
        openErrorModal({ message: e.message });
      }
      reportAppError(e);
    }
  });

  const hideListingsTerminology = state.referrer === OPERATIONS_SHIFTS_PATH;

  return {
    closeErrorModal,
    closeWarningModal,
    control,
    errorModalData,
    floorPlans,
    handleOnClickSave,
    handleOnClickConfirm,
    hideListingsTerminology,
    isErrorModalOpen,
    isWarningModalOpen,
    referrer: state.referrer,
    warningModalData,
    setValue,
    resetField,
  };
};
